import { Form } from 'components';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, useBradManagementQuery, useBrandManagementUpdateMutation } from 'api';
import { IManagementUpdate } from './index';

export const useEditManagement = (props: IManagementUpdate) => {
  const { onCancel, openUpdateModal } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: brandDetails = {} } = useBradManagementQuery({ id });
  const { data: userData } = useAdminUsersQuery();
  const [updateManagement, { isLoading }] = useBrandManagementUpdateMutation();

  const initialManagement: Record<string, string> = useMemo(
    () => ({
      purchaseManagerId: brandDetails.purchaseManager?.id,
      postSalesManagerId: brandDetails.postSalesManager?.id,
      postSalesManagerAssistantId: brandDetails.postSalesAssistantManager?.id,
      purchaseManagerAssistantId: brandDetails.purchaseAssistantManager?.id,
    }),
    [brandDetails]
  );

  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );
  const onModalCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialManagement[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }

      payload.brandId = id as string;
      const { data, error }: Record<string, any> = await updateManagement(payload);
      if (data?.success) {
        handleSuccess('Brand management was successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [updateManagement, handleError, handleSuccess, id, onCancel, form, initialManagement]);

  useEffect(() => {
    if (initialManagement && openUpdateModal) {
      form.setFieldsValue(initialManagement);
    }
  }, [form, openUpdateModal, initialManagement]);

  return {
    onFinish,
    userData,
    form,
    isLoading,
    onModalCancel,
    handleManagerChange,
  };
};
